import { ConfigProvider } from "antd";
import { API_URL } from "env";
import Layout from "Layout";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
// @ts-ignore: Allow importing SCSS modules without type declarations
import '../inventory.scss';
import { toast } from "react-toastify";

const platformMapingData = [
    { key: 'MT4', name: 'MetaTrader 4', color: 'bg-blue-500' },
    { key: 'MT5', name: 'MetaTrader 5', color: 'bg-green-500' },
    { key: 'cTrader', name: 'cTrader', color: 'bg-purple-500' },
]

const InventoryDetail = () => {
    const { t } = useTranslation();
    const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
    const id = window.location.pathname.split('/').pop();
    const [lang, setLang] = useState('en');
    const [productType, setProductType] = useState<'tool' | 'product' | null>(null);
    const [product, setProduct] = useState<any>(null);
    const [paymentDetail, setPaymentDetail] = useState<any>(null);
    async function fetchInventoryDetail() {
        try {
            if (!user || !user.id) return;
            const response = await fetch(API_URL + `/api/v1/be/orders/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                },
            });
            const data = await response.json();
            if (data.status) {
                getPurchaseDetail(data.data.id);
                if (data.data.tool_id) {
                    setProductType('tool');
                    const dataTool = await getDetailTool(data.data.tool_id);
                }
                else if (data.data.product_id) {
                    setProductType('product');
                }
            } else {
                console.error("Response not ok" + response.statusText);
            }
        }
        catch (error) {
            console.error("Error fetching inventory detail:", error);
        }
    }
    async function getDetailTool(toolId: number) {
        try {
            const response = await fetch(API_URL + `/api/v1/be/tools/${toolId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                },
            });
            const data = await response.json();
            if (data.status) {
                setProduct(data.data);
            } else {
                console.error("Response not ok" + response.statusText);
            }
        }
        catch (error) {
            console.error("Error fetching tool detail:", error);
        }
    }

    async function getPurchaseDetail(orderId: number) {
        try {
            const response = await fetch(API_URL + `/api/v1/be/payments/${orderId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                },
            });
            const data = await response.json();
            if (data.status) {
                setPaymentDetail(data.data);
            } else {
                console.error("Response not ok" + data.error);
            }
        }
        catch (error) {
            console.error("Error fetching purchase detail:", error);
        }
    }

    function downloadFromLink(url: string, filename: string) {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async function downloadFromBase64(base64Data: string, filename: string) {
        if (!base64Data) return;
        try {
            // If the input is an HTTP(s) URL, delegate to downloadFromLink
            if (/^https?:\/\//i.test(base64Data)) {
                downloadFromLink(base64Data, filename);
                return;
            }

            // If input is a data URL, extract mime and base64 payload
            let mime = 'application/octet-stream';
            let b64 = base64Data;
            const dataUrlMatch = base64Data.match(/^data:([^;]+);base64,(.*)$/);
            if (dataUrlMatch) {
                mime = dataUrlMatch[1];
                b64 = dataUrlMatch[2];
            }

            // Clean base64 string: remove whitespace/newlines and convert URL-safe chars
            b64 = b64.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');

            // Fix padding if necessary
            const pad = b64.length % 4;
            if (pad > 0) {
                b64 += '='.repeat(4 - pad);
            }

            let blob: Blob | null = null;

            // Try decoding with atob first
            try {
                const binaryString = atob(b64);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                blob = new Blob([bytes], { type: mime });
            } catch (decodeError) {
                // Fallback: construct a data URL and fetch it to obtain a blob
                try {
                    const dataUrl = `data:${mime};base64,${b64}`;
                    const res = await fetch(dataUrl);
                    blob = await res.blob();
                } catch (fetchError) {
                    console.error('Error decoding base64 file (both atob and fetch failed):', fetchError);
                    return;
                }
            }

            if (!blob) return;

            // Create object URL and trigger download
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Cleanup
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.error('Error downloading base64 file:', error);
        }
    }

    useEffect(() => {
        fetchInventoryDetail();
    }, [id]);
    return (
        <Layout>
            <Helmet>
                <title>Product Detail</title>
                <meta name="description" content="User inventory of purchased products from the store." />
                <link rel="canonical" href="/stores/inventory" />
                <meta name="keywords" content="Store Inventory, Purchased Products, User Store, Trading Bots Inventory" />
                <meta property="og:title" content="Store - Inventory" />
                <meta property="og:description" content="User inventory of purchased products from the store." />
                <meta property="og:url" content="/stores/inventory" />
                <meta property="og:type" content="website" />
            </Helmet>
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimaryActive: '#E9B460',
                        colorPrimaryHover: '#E9B460',
                        colorPrimary: '#E9B460',
                        borderRadius: 6,
                    },
                    components: {
                        Radio: {
                            buttonSolidCheckedActiveBg: '#E9B460',
                            buttonSolidCheckedBg: '#E9B460',
                            buttonSolidCheckedColor: '#fff',
                            buttonSolidCheckedHoverBg: '#E9B460',
                        },
                    },
                }}
            >
                <nav className="breadcrumb__container mb-3">
                    <div className="breadcrumb__gradient breadcrumb__gradient--left" data-gradient="left" style={{ display: 'none' }}></div>
                    <ul className="breadcrumb" data-breadcrumb-scroll-container="">
                        <li className="breadcrumb-item"><a href="/accounts-overview">{t('Trader')}</a></li>
                        <li className="breadcrumb-item ">
                            <a href="/profile">
                                {t('Profile')}
                            </a>
                        </li>
                        <li className="breadcrumb-item active">{t('PersonalInformation')}</li>
                    </ul>
                    <div className="breadcrumb__gradient breadcrumb__gradient--right" data-gradient="right" style={{ display: 'none' }}></div>
                </nav>

                <div className="section-header mb-4">
                    <div className="section-title">
                        <div className="section-title__title-wrap">
                            <div className="section-title__icon">
                                <i className="fi fi--user fi--themed"></i>
                            </div>
                            <div className="section-title--main">{t('Profile')}</div>
                        </div>
                    </div>
                    <div className="section-header__tabs">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <a href="/profile" className="nav-link ">
                                    <span className="nav-link__label">{t('PersonalInformation')}</span>
                                </a>
                            </li>
                            <li className="nav-item">
                                <a href="/profile/account" className="nav-link ">
                                    <span className="nav-link__label">{t('AccountInformation')}</span>
                                </a>
                            </li>
                            <li className="nav-item">
                                <a href="/profile/security" className="nav-link ">
                                    <span className="nav-link__label">{t('Security')}</span>
                                </a>
                            </li>
                            {/* <li className="nav-item">
                                <a href="/profile/teams" className="nav-link ">
                                    <span className="nav-link__label">{t('Teams')}</span>
                                </a>
                            </li> */}
                            <li className="nav-item">
                                <a href="/profile/affiliate" className="nav-link ">
                                    <span className="nav-link__label">{t('Affiliates')}</span>
                                </a>
                            </li>
                            <li className="nav-item">
                                <a href="/profile/inventory" className="nav-link active">
                                    <span className="nav-link__label">{t('Inventory')}</span>
                                </a>
                            </li>
                            <li className="nav-item" style={{ pointerEvents: 'none', cursor: 'default', color: 'gray', opacity: '0.6' }}>
                                <a href="#" data-notification-target="nav-profile-identity" className="nav-link disabled" tabIndex={-1} aria-disabled="true">
                                    <span className="nav-link__label">{t('LUMIRIdentity')}</span>
                                </a>
                            </li>
                            <li className="nav-item" style={{ pointerEvents: 'none', cursor: 'default', color: 'gray', opacity: '0.6' }}>
                                <a href="#" className="nav-link disabled" tabIndex={-1} aria-disabled="true">
                                    <span className="nav-link__label">{t('EmailNewsletters')}</span>
                                </a>
                            </li>
                            <li className="nav-item" style={{ pointerEvents: 'none', cursor: 'default', color: 'gray', opacity: '0.6' }}>
                                <a href="#" className="nav-link disabled" tabIndex={-1} aria-disabled="true">
                                    <span className="nav-link__label">{t('LUMIRPoints')}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-6xl">
                    <div className="flex flex-row gap-4 w-full">
                        <div className="flex items-center justify-center cursor-pointer inventory-detail-section w-16 rounded-[3px] shadow-sm border mb-6" onClick={() => window.location.href = '/profile/inventory'}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256"><path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"></path></svg>
                        </div>
                        <div className="inventory-detail-section rounded-[3px] shadow-sm border mb-6 w-full">
                            <div className="p-6">

                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-4 relative">
                                        <div className="relative w-16 h-16 flex-shrink-0">
                                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 img-loading">
                                                <img src="/loading.svg" alt="loading" className="w-16 h-16" />
                                            </div>
                                            <img
                                                src={product?.image ? product?.image : 'https://via.placeholder.com/150'}
                                                alt="ScalperPro EA"
                                                className="w-16 h-16 rounded-lg object-cover"
                                                onLoad={e => {
                                                    const target = e.currentTarget as HTMLImageElement;
                                                    target.style.transition = 'opacity 300ms ease-in';
                                                    target.style.opacity = '1';

                                                    // remove any loading placeholder/overlay if present
                                                    const placeholder = target.parentElement?.querySelector('.img-loading, .loading-svg, .loading-placeholder') as HTMLElement | null;
                                                    if (placeholder) placeholder.remove();
                                                }}
                                                onError={e => {
                                                    const target = e.currentTarget as HTMLImageElement;
                                                    // prevent repeated error handling
                                                    target.onerror = null;
                                                    // hide the broken image element
                                                    target.style.display = 'none';

                                                    // remove any loading placeholder/overlay if present
                                                    const placeholder = target.parentElement?.querySelector('.img-loading, .loading-svg, .loading-placeholder') as HTMLElement | null;
                                                    if (placeholder) placeholder.remove();

                                                    // only add one fallback element
                                                    if (!target.parentElement?.querySelector('.img-fallback')) {
                                                        const fallback = document.createElement('div');
                                                        fallback.className = "img-fallback h-full inset-0 flex items-center justify-center bg-gray-100";
                                                        fallback.innerHTML = `
                                                        <div class="flex flex-col items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256"><path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16h64a8,8,0,0,0,7.59-5.47l14.83-44.48L163,151.43a8.07,8.07,0,0,0,4.46-4.46l14.62-36.55,44.48-14.83A8,8,0,0,0,232,88V56A16,16,0,0,0,216,40ZM112.41,157.47,98.23,200H40V172l52-52,30.42,30.42L117,152.57A8,8,0,0,0,112.41,157.47ZM216,82.23,173.47,96.41a8,8,0,0,0-4.9,4.62l-14.72,36.82L138.58,144l-35.27-35.27a16,16,0,0,0-22.62,0L40,149.37V56H216Zm12.68,33a8,8,0,0,0-7.21-1.1l-23.8,7.94a8,8,0,0,0-4.9,4.61l-14.31,35.77-35.77,14.31a8,8,0,0,0-4.61,4.9l-7.94,23.8A8,8,0,0,0,137.73,216H216a16,16,0,0,0,16-16V121.73A8,8,0,0,0,228.68,115.24ZM216,200H148.83l3.25-9.75,35.51-14.2a8.07,8.07,0,0,0,4.46-4.46l14.2-35.51,9.75-3.25Z"></path></svg>    
                                                        </div>    
                                                    `;
                                                        target.parentNode?.appendChild(fallback);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex flex-col">
                                            <h2 className="!text-xl font-semibold text mb-1">{
                                                product?.title[lang] || 'Unknow Name'
                                            }</h2>
                                            <div className="flex items-center gap-3">
                                                <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${platformMapingData.find(p => p.key === product?.platform)?.color || 'bg-gray-500'
                                                    } text-white`}>
                                                    {platformMapingData.find(p => p.key === product?.platform)?.name || product?.platform || 'Unknown Platform'}
                                                </div>
                                                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-green-50 text-green-700 border-green-200">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-green-500"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><path d="m9 11 3 3L22 4"></path></svg>
                                                    <span className="ml-1 capitalize">
                                                        {paymentDetail?.status || 'Unknown Status'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <div className="text-right">
                                        <div className="!text-2xl font-bold text-muted">$299</div>
                                        <div className="!text-sm text-gray-500 line-through">$399</div>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="inventory-detail-section rounded-[3px] shadow-sm border">
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div>
                                            <h3 className="!text-lg font-semibold text mb-4 flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-hash h-5 w-5 text-gray-500"><line x1="4" x2="20" y1="9" y2="9"></line><line x1="4" x2="20" y1="15" y2="15"></line><line x1="10" x2="8" y1="3" y2="21"></line><line x1="16" x2="14" y1="3" y2="21"></line></svg>
                                                Transaction Details
                                            </h3>
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-sm text">Transaction ID:</span>
                                                    <span className="text-sm inline-flex gap-2 font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
                                                        {paymentDetail?.external_ref || 'N/A'} 
                                                        <svg
                                                        onClick={() => {
                                                            if (paymentDetail?.external_ref) {
                                                                navigator.clipboard.writeText(paymentDetail.external_ref);
                                                                toast.success('Transaction ID copied to clipboard');
                                                            }
                                                        }}
                                                        className="cursor-pointer"
                                                        xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256"><path d="M216,32H88a8,8,0,0,0-8,8V80H40a8,8,0,0,0-8,8V216a8,8,0,0,0,8,8H168a8,8,0,0,0,8-8V176h40a8,8,0,0,0,8-8V40A8,8,0,0,0,216,32ZM160,208H48V96H160Zm48-48H176V88a8,8,0,0,0-8-8H96V48H208Z"></path></svg>
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-sm text">Date:</span>
                                                    <span className="text-sm font-medium text-muted">
                                                        {paymentDetail?.updated_at ? new Date(paymentDetail.updated_at).toLocaleString(lang) : 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center py-2">
                                                    <span className="text-sm text">Method:</span>
                                                    <div className="flex items-center gap-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card h-4 w-4 text-blue-600"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                                                        <span className="text-sm font-medium text-muted capitalize">paypal</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="!text-lg font-semibold text mb-4 flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user h-5 w-5 text-gray-500"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                                Customer Info
                                            </h3>
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-sm text">Order Number:</span>
                                                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">{paymentDetail?.id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between items-start py-2 border-b border-gray-100">
                                                    <span className="text-sm text">Email:</span>
                                                    <span className="text-sm font-medium text-muted text-right max-w-48 break-all">
                                                        {paymentDetail?.meta.paypal_response?.payer?.email_address || 'N/A'}
                                                    </span>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="inventory-detail-section rounded-[3px] shadow-sm border">
                                <div className="p-6">
                                    <h3 className="!text-lg font-semibold text mb-4 flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-package h-5 w-5 text-gray-500"><path d="m7.5 4.27 9 5.15"></path><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path><path d="m3.3 7 8.7 5 8.7-5"></path><path d="M12 22V12"></path></svg>
                                        Available Downloads
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <a
                                            onClick={() => downloadFromLink(product.file_bot_url, product.title.en + (product.platform === 'MT4' ? '.ex4' : product.platform === 'MT5' ? '.ex5' : product.platform === 'cTrader' ? '.cbot' : ''))}
                                            style={
                                                !product?.file_bot_url ? { pointerEvents: 'none', cursor: 'default', opacity: '0.5' } : {}
                                            }
                                            className="group-file flex items-center gap-3 p-4 border border-gray-200 rounded-lg transition duration-200">
                                            <div className="p-2 bg-blue-100 rounded-[3px] transition duration-200">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download h-5 w-5 text-blue-600"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
                                            </div>
                                            <div>
                                                <div className="font-medium text">Bot File</div>
                                                <div className="text-xs text-gray-500">.ex4/.ex5/.cbot</div>
                                            </div>
                                        </a>
                                        <a href={product?.file_pdf_url?.en}
                                            style={
                                                !product?.file_pdf_url?.en ? { pointerEvents: 'none', cursor: 'default', opacity: '0.5' } : {}
                                            }
                                            className="group-pdf flex items-center gap-3 p-4 border border-gray-200 rounded-lg transition duration-200">
                                            <div className="p-2 bg-green-100 rounded-[3px] transition duration-200">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text h-5 w-5 text-green-600"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>
                                            </div>
                                            <div>
                                                <div className="font-medium text">User Manual</div>
                                                <div className="text-xs text-gray-500">PDF Guide</div>
                                            </div>
                                        </a>
                                        <a href={product?.video?.en}
                                            style={
                                                !product?.video?.en ? { pointerEvents: 'none', cursor: 'default', opacity: '0.5' } : {}
                                            }
                                            target="_blank" rel="noopener noreferrer" className="group-video flex items-center gap-3 p-4 border border-gray-200 rounded-lg transition duration-200 cursor-pointer">
                                            <div className="p-2 bg-red-100 rounded-[3px] transition duration-200">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-play h-5 w-5 text-red-600"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                            </div>
                                            <div>
                                                <div className="font-medium text">Video Tutorial</div>
                                                <div className="text-xs text-gray-500">YouTube</div>
                                            </div>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-1">
                            <div className="sticky top-8 space-y-6">
                                <div className="inventory-detail-section rounded-[3px] shadow-sm border">
                                    <div className="p-6">
                                        <h3 className="!text-lg font-semibold text mb-4">Payment Summary</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text ">Product Price:</span>
                                                <span className="font-semibold text text-sm ">
                                                    {(product?.price || '0') + ' ' + (paymentDetail?.currency || 'USD')}
                                                </span>
                                            </div>
                                            {
                                                product?.price_discount && (
                                                    <div className="flex justify-between items-center text-green-600">
                                                        <span>Discount:</span>
                                                        <span className="font-semibold">
                                                            {product?.price_discount ? `- ${product.price_discount} ${paymentDetail?.currency}` : '$0'}
                                                        </span>
                                                    </div>
                                                )
                                            }
                                            <div className="flex justify-between items-center text-lg">
                                                <span>Price:</span>
                                                <span className="">
                                                    {(product?.price_finalize || '0') + ' ' + (paymentDetail?.currency || "USD")}
                                                </span>
                                            </div>
                                            <div className="border-t pt-3">
                                                <div className="flex justify-between items-center text-lg font-bold">
                                                    <span>Total Paid:</span>
                                                    <span className="text-blue-600">
                                                        {paymentDetail?.amount_formatted || 'Free'}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-500 line-through">
                                                        {product?.price_finalize || ''} {paymentDetail?.currency}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-[3px] border border-blue-200">
                                    <div className="p-6 support">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield h-5 w-5 text-blue-600"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path></svg>
                                            <h3 className="font-semibold text-blue-900 invert-text !mb-0 !text-lg">Secure &amp; Supported</h3>
                                        </div>
                                        <div className="space-y-2 text-sm text-blue-800">
                                            <div className="flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-4 w-4 text-green-600"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><path d="m9 11 3 3L22 4"></path></svg>
                                                <span>Lifetime technical support</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-4 w-4 text-green-600"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><path d="m9 11 3 3L22 4"></path></svg>
                                                <span>Free updates included</span>
                                            </div>
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-blue-200">
                                            <h4 className="font-medium text-blue-900 invert-text mb-2">Need Help?</h4>
                                            <div className="space-y-1 text-sm text-blue-700">
                                                <div className="flex items-center gap-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mail h-4 w-4"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
                                                    <span>support@lumir.finance</span>
                                                </div>
                                                <div>📱 +84 123 456 789</div>
                                                <div>💬 24/7 Live Chat</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ConfigProvider>
        </Layout>

    );
};

export default InventoryDetail;